import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getAlternateRoute } from '../utils/routeMap';
import { useDataStore } from '../store/dataStore';

interface SEOProps {
    pageCode?: string; // e.g. 'home', 'about', 'contact'
    title?: string;
    description?: string;
    link: string;
    type?: 'website' | 'article';
    name?: string;
    image?: string;
    noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
    pageCode,
    title,
    description,
    link,
    type = 'website',
    name = 'Pato Yazılım',
    image,
    noIndex = false
}) => {
    const location = useLocation();
    const { siteSettings, seoSettings } = useDataStore();
    const isEn = location.pathname.startsWith('/en');
    const lang = isEn ? 'EN' : 'TR';

    // Find custom SEO settings from panel if pageCode is provided
    const customSeo = pageCode ? seoSettings?.find(s => s.page === pageCode && s.language === lang) : null;
    
    // Merge props with custom settings (custom settings take precedence)
    const finalTitle = customSeo?.title || title || 'Pato Yazılım';
    const finalDesc = customSeo?.description || description || '';
    const finalKeywords = customSeo?.keywords || '';
    const siteUrl = 'https://patoyazilim.com';
    // Ensure link starts with / if not provided, handle empty link for home
    const normalizedLink = link === '/' ? '' : (link.startsWith('/') ? link : `/${link}`);
    // If link is empty (home), fullUrl should be just siteUrl
    // Actually, for home page, link might be passed as '/' or ''. 
    // If link is '/', fullUrl -> https://patoyazilim.com/
    // Let's stick to standard behavior: if link is '/', result is https://patoyazilim.com/

    // Simpler approach:
    // User example: link rel="canonical" href={`https://patoyazilim.com${link}`}
    // So if link is '/', result is ...com/

    const fullUrl = `https://patoyazilim.com${link}`;
    const metaImage = image || `https://patoyazilim.com/images/sosyal-medya-kapak.png`;

    return (
        <Helmet>
            {/* Sayfa Başlığı */}
            <title>{finalTitle} | Pato Yazılım</title>

            {/* Sayfa Açıklaması */}
            <meta name='description' content={finalDesc} />
            {finalKeywords && <meta name="keywords" content={finalKeywords} />}

            {/* Robots - Indexing Control */}
            {noIndex ? (
                <meta name="robots" content="noindex, follow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* DİNAMİK CANONICAL URL */}
            <link rel="canonical" href={fullUrl} />

            {/* Sosyal Medya */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={`${finalTitle} | Pato Yazılım`} />
            <meta property="og:description" content={finalDesc} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:site_name" content={name} />
            <meta property="og:image" content={metaImage} />

            {/* hreflang SEO Etiketleri */}
            <link rel="alternate" hreflang="tr" href={`https://patoyazilim.com${getAlternateRoute(location.pathname, 'tr')}`} />
            <link rel="alternate" hreflang="en" href={`https://patoyazilim.com${getAlternateRoute(location.pathname, 'en')}`} />
            <link rel="alternate" hreflang="x-default" href={`https://patoyazilim.com${getAlternateRoute(location.pathname, 'tr')}`} />

            {/* Twitter tags */}
            <meta name="twitter:creator" content={name} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${finalTitle} | Pato Yazılım`} />
            <meta name="twitter:description" content={finalDesc} />
            <meta name="twitter:image" content={metaImage} />

            {/* Google Analytics */}
            {siteSettings?.gaId && (
                <script async src={`https://www.googletagmanager.com/gtag/js?id=${siteSettings.gaId}`}></script>
            )}
            {siteSettings?.gaId && (
                <script>
                    {`
                      window.dataLayer = window.dataLayer || [];
                      function gtag(){dataLayer.push(arguments);}
                      gtag('js', new Date());
                      gtag('config', '${siteSettings.gaId}');
                    `}
                </script>
            )}

            {/* Microsoft Clarity */}
            {siteSettings?.clarityId && (
                <script type="text/javascript">
                    {`
                      (function(c,l,a,r,i,t,y){
                          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                      })(window, document, "clarity", "script", "${siteSettings.clarityId}");
                    `}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
